import { useCallback, useMemo, useState } from 'react';
import { Box, Text, Menu, MenuButton, MenuItem, MenuList, Button } from '@chakra-ui/react';
import PaginationComponent from '../../Pagination';
import NoDataComponent from '../../NoData';
import TableComponent from '../../Table';
import InputSearchComponent from '../../Common/Form/input-search';
import ImageComponent from '../../Common/Image';
import { useNavigate } from 'react-router-dom';
import SeedIcon from '../../../assets/seed-icon.svg';
import LearnMorePanel from '../../../routes/ArticleViewer/components/LearnMorePanel';
import NoResultImg from '../../../assets/no-result.svg';
import ChevronDownIcon from '../../../assets/chevron-down-green-icon.svg';
import EyeGreenIcon from '../../../assets/eye-light-green-icon.svg';
import AvatarDefault from '../../../assets/medium_default_optimized.png';
import MyInvestorsDetailModal from './my-investors-detail-modal.component';

const statusList = ['Active', 'Frozen'];

export const MyInvestorsTableComponent = (props) => {
  const { dataTable, pagination, setPagination, refetch, totalPage, isFetching } = props;

  const navigate = useNavigate();

  const [modal, setModal] = useState('');
  const [openLearnMorePanel, setOpenLearnMorePanel] = useState(false);
  const [status, setStatus] = useState('');
  const [investorId, setInvestorId] = useState();

  const handleChangePage = (page) => {
    setPagination((pre) => {
      return {
        ...pre,
        page
      };
    });
  };

  const handleChangeSearch = (search) => {
    setPagination((pre) => {
      return {
        ...pre,
        page: 1,
        search: search
      };
    });
  };

  const convertInvestor = useCallback((row) => {
    return (
      <Box
        display={'flex'}
        alignItems={'center'}
        width={{ base: '100vw', md: 'unset' }}
        cursor={'pointer'}
        onClick={() => navigate(`/${row?.investor?.username}`)}
      >
        <Box position={'relative'} flexShrink={0}>
          <ImageComponent
            rounded={'12px'}
            w={'40px'}
            h={'40px'}
            src={row?.investor?.imgUrl || AvatarDefault}
            alt="cover"
          />
        </Box>
        <Box ml={'12px'} display={'flex'} flexDir={'column'}>
          <Text
            fontWeight={800}
            fontSize={'xs'}
            title={row?.investor?.firstName}
            overflow={'hidden'}
            textOverflow={'ellipsis'}
            wordBreak={'break-word'}
            sx={{
              display: '-webkit-inline-box',
              WebkitLineClamp: '1',
              WebkitBoxOrient: 'vertical'
            }}
            _hover={{
              textDecoration: 'underline'
            }}
          >
            {row?.investor?.firstName || 'Untitled'}
          </Text>
          <Text
            fontWeight={600}
            fontSize={10}
            title={row?.investor?.firstName}
            overflow={'hidden'}
            textOverflow={'ellipsis'}
            wordBreak={'break-word'}
            textColor={'#808080'}
            sx={{
              display: '-webkit-inline-box',
              WebkitLineClamp: '1',
              WebkitBoxOrient: 'vertical'
            }}
            _hover={{
              textDecoration: 'underline'
            }}
          >
            {'@' + row?.investor?.username}
          </Text>
        </Box>
      </Box>
    );
  }, []);

  const convertMyArticles = useCallback((row) => {
    return (
      <Box display={'flex'} alignItems={'center'} gap={1}>
        <Text fontSize={'xs'} fontWeight={600} whiteSpace={'nowrap'}>
          {row?.totalArticleInvested || 0} {row?.totalInvestors > 1 ? 'Articles' : 'Article'}
        </Text>
        <img
          src={EyeGreenIcon}
          alt=""
          className="cursor-pointer"
          onClick={() => {
            setModal('my-investor-detail');
            setInvestorId(row?.investor?.id);
          }}
        />
      </Box>
    );
  }, []);

  const convertInvestorAverageShare = useCallback((row) => {
    return (
      <Text color="#204A22" fontWeight={800} fontSize={'xs'}>
        {row.averageShare}% <span className="font-[400]"> of ad revenue pool </span>
      </Text>
    );
  }, []);

  const convertPointInvested = useCallback((row) => {
    return (
      <Box display={'flex'} alignItems={'center'} gap={1}>
        <img src={SeedIcon} className="w-5 h-5" alt="" />
        <Text fontSize={'xs'} fontWeight={800}>
          {row.totalSeedInvested || 0}
        </Text>
      </Box>
    );
  }, []);

  const convertStatus = useCallback((row) => {
    return (
      <Box
        py={2}
        px={3}
        bgColor={row.status === 'Active' ? 'growth-green' : 'error-red'}
        rounded={'80px'}
        w={'fit-content'}
      >
        <Text fontSize={'xs'} color={'white'} fontWeight={800}>
          {row.status}
        </Text>
      </Box>
    );
  }, []);

  const handleSort = (sortName) => {
    let objSort = {
      sortBy: pagination.sortBy,
      sort: pagination.sort
    };
    if (sortName !== objSort.sortBy) objSort = { sortBy: sortName, sort: 'asc' };
    else if (sortName === objSort.sortBy && objSort.sort === 'asc')
      objSort = {
        ...objSort,
        sort: 'desc'
      };
    else if (sortName === objSort.sortBy && objSort.sort === 'desc')
      objSort = {
        ...objSort,
        sort: 'asc'
      };
    setPagination({ ...pagination, ...objSort });
  };

  const columns = useMemo(
    () => [
      {
        Header: 'Investor',
        accessor: (row) => convertInvestor(row),
        id: 'investor',
        widthCustom: '9rem',
        minWidthCustom: '9rem'
      },
      {
        Header: 'My Article(s)',
        accessor: (row) => convertMyArticles(row),
        id: 'myArticles',
        widthCustom: '11rem',
        minWidthCustom: '11rem'
      },
      {
        Header: 'Investor’s average share',
        accessor: (row) => convertInvestorAverageShare(row),
        id: 'currentShare',
        widthCustom: '11rem',
        minWidthCustom: '11rem',
        isSortField: true
      },
      {
        Header: 'Total Seeds invested',
        accessor: (row) => convertPointInvested(row),
        id: 'pointInvested',
        widthCustom: '10rem',
        minWidthCustom: '10rem',
        isSortField: true
      },
      {
        Header: 'Status',
        accessor: (row) => convertStatus(row),
        id: 'status',
        widthCustom: '6rem',
        minWidthCustom: '6rem'
      }
    ],

    [convertInvestorAverageShare, handleSort]
  );

  return (
    <Box
      w={'full'}
      border={'1px solid'}
      borderColor={'anti-flash-lite'}
      borderRadius={{ base: 0, md: '12px' }}
      shadow={'outline'}
    >
      <Box
        bg={'white'}
        p={{ base: '16px', md: '16px 24px' }}
        display={'flex'}
        alignItems={'start'}
        minH={'72px'}
        borderTopRadius={'12px'}
        justifyContent={'space-between'}
        overflow={'auto'}
      >
        <Box
          display={'flex'}
          gap="12px"
          flexDirection={{ base: 'column', md: 'row' }}
          width={'full'}
          overflow={'auto'}
        >
          <InputSearchComponent
            setPaginationSearch={handleChangeSearch}
            placeholder={'Search Article or Investor name'}
            setLoading={() => {}}
            width={{ base: '100%', md: '320px' }}
          />
          <Menu>
            <MenuButton
              as={Button}
              rightIcon={<img src={ChevronDownIcon} alt="icon" />}
              w={'fit-content'}
              height={'36px'}
              variant={'growth-green-solid'}
              px={'18px'}
            >
              <Text color={'#4CAF50'} fontWeight={900} fontSize={'sm'} textAlign={'start'}>
                {status ? status : 'Status'}
              </Text>
            </MenuButton>
            <MenuList
              p={'0.5rem'}
              borderRadius={'0.5rem'}
              minW={'171px'}
              maxH={'300px'}
              overflow={'auto'}
              css={{
                '&::-webkit-scrollbar': {
                  width: '6px'
                },
                '&::-webkit-scrollbar-thumb': {
                  background: '#e6e7eb',
                  borderRadius: '30px'
                }
              }}
              shadow={'md'}
              border={'1px solid'}
              borderColor={'light-gray'}
            >
              {['All Status', ...statusList].map((item, index) => (
                <MenuItem
                  key={index}
                  p={'8px 8px 8px 10px'}
                  _focus={{ bgColor: 'white' }}
                  _hover={{
                    bgColor: 'anti-flash-white',
                    '& .child-box': {
                      display: 'block'
                    }
                  }}
                  onClick={() => {
                    setStatus(item);
                    if (item === 'All Status') {
                      setPagination({ ...pagination, status: undefined, page: 1 });
                    } else setPagination({ ...pagination, status: item, page: 1 });
                  }}
                  rounded={'0.5rem'}
                  fontSize={'14px'}
                  lineHeight={'20px'}
                  fontWeight={'800'}
                >
                  {item}
                </MenuItem>
              ))}
            </MenuList>
          </Menu>
        </Box>
      </Box>
      <Box
        bg={'white'}
        rounded={'0 0 0.75rem 0.75rem'}
        display={'flex'}
        minH={'370px'}
        flexFlow={'column'}
        overflowY={'hidden'}
        overflowX={'auto'}
        css={{
          '&::-webkit-scrollbar': {
            display: 'block',
            height: '8px' // For horizontal scrollbar
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#e6e7eb',
            borderRadius: '30px'
          },
          '&::-webkit-scrollbar-track': {
            background: '#f5f5f5'
          }
        }}
      >
        <TableComponent
          data-testid="my-investors-table"
          isLoading={isFetching}
          columns={columns}
          data={dataTable ?? []}
          headerHeight={'46px'}
          fontsizeTable={'sm'}
          handleSort={handleSort}
          currentSort={{ sortBy: pagination.sortBy, sort: pagination.sort }}
          haveFooter={false}
        />
        {!pagination.search && !pagination.status && dataTable?.length === 0 && !isFetching ? (
          <Box minH={'370px'} display={'flex'} alignItems={'center'} justifyContent={'center'}>
            <NoDataComponent
              title="It looks like you haven't any investors yet."
              description="Try to improve your content to find more creators!"
              titleButton="Discover Articles"
              onClick={() => navigate('/')}
              isFetching={isFetching}
            />
          </Box>
        ) : null}
        {(pagination.search || pagination.status) && dataTable?.length === 0 && !isFetching ? (
          <Box minH={'370px'} display={'flex'} alignItems={'center'} justifyContent={'center'}>
            <NoDataComponent
              icon={NoResultImg}
              title="No results found"
              description="Try a different search term"
              isFetching={isFetching}
            />
          </Box>
        ) : null}
      </Box>

      {totalPage > 0 ? (
        <Box
          borderBottomRadius={'12px'}
          p={'1.25rem 1.5rem'}
          mt={'auto'}
          borderTop="1px solid"
          borderColor={'anti-flash-lite'}
          background={'light.100'}
        >
          <PaginationComponent
            currentPage={pagination.page}
            totalPages={totalPage}
            onChangePage={handleChangePage}
          />
        </Box>
      ) : null}
      {modal === 'my-investor-detail' ? (
        <MyInvestorsDetailModal
          investorId={investorId}
          refetch={refetch}
          setOpenLearnMorePanel={setOpenLearnMorePanel}
          isOpen
          onClose={() => setModal('')}
        />
      ) : null}
      {openLearnMorePanel && <LearnMorePanel isOpen onClose={() => setOpenLearnMorePanel(false)} />}
    </Box>
  );
};
